import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getTSectionVO,
    saveTSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    T2_PAGE,
    T2_2_PAGE,
    TS_PAGE,
    T_FORM_PAGES,
    T_PAGE_TITLES,
    T_SECTION_PAGES,
    TFormPage,
    TProgrammePage,
    TSectionPageCode,
    TSummaryPage,
} from '@/pages/components/audit-program/t-section/constants';
import TsSummaryView from '@/pages/components/audit-program/t-section/TsSummaryView';
import TFormView from '@/pages/components/audit-program/t-section/TFormView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type TSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: TSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

function isProgramme(code: TSectionPageCode): boolean {
    return code === 'T2' || code === 'T2-2';
}

function isForm(code: TSectionPageCode): boolean {
    return Boolean(T_FORM_PAGES[code]);
}

export default function TSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Ts',
}: TSectionViewProps) {
    const [pageKey, setPageKey] = useState<TSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('T');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getTSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of T_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load T Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: TSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveTSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const tsContent = (getContent('Ts') ?? TS_PAGE) as TSummaryPage;
    const t2Content = (getContent('T2') ?? T2_PAGE) as TProgrammePage;
    const t22Content = (getContent('T2-2') ?? T2_2_PAGE) as TProgrammePage;

    const formContents = useMemo(() => {
        const map: Partial<Record<TSectionPageCode, TFormPage>> = {};
        for (const code of T_SECTION_PAGES) {
            const fallback = T_FORM_PAGES[code];
            if (!fallback) continue;
            map[code] = (getContent(code) ?? fallback) as TFormPage;
        }
        return map;
    }, [getContent]);

    const tabs = useMemo(() => T_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={T_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Ts' ? (
                <TsSummaryView
                    header={header}
                    page={tsContent}
                    values={pageData.Ts ?? {}}
                    onChange={(k, v) => handleFieldChange('Ts', k, v)}
                />
            ) : isProgramme(code) ? (
                <G0ProgrammeView
                    header={header}
                    page={code === 'T2' ? t2Content : t22Content}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : isForm(code) && formContents[code] ? (
                <TFormView
                    header={header}
                    page={formContents[code]!}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : null
        ) : null,
    })), [header, pageData, handleFieldChange, tsContent, t2Content, t22Content, formContents]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    T Section — Subsequent events
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as TSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
